import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import {
  Archive,
  ArrowLeft,
  Cake,
  CalendarPlus,
  Car,
  Mail,
  Pencil,
  Phone,
  Trash2,
  User as UserIcon,
} from 'lucide-react'

import { ApiError } from '@/api/client'
import { studentsApi } from '@/api/endpoints'
import { useAuth } from '@/auth/AuthContext'
import { isTeacher } from '@/auth/permissions'
import { PageHeader } from '@/components/layout/AppLayout'
import { Alert } from '@/components/ui/Alert'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import {
  InstrumentBadges,
  SkillLevelBadge,
  StudentStatusBadge,
} from '@/components/ui/InstrumentBadge'
import { ConfirmDialog } from '@/components/ui/Modal'
import { FullScreenLoader } from '@/components/ui/Spinner'
import { useToast } from '@/components/ui/Toast'
import { ageFrom } from '@/lib/dictionaries'
import { formatDate, formatDateTime, plural } from '@/lib/format'
import { StudentBreaks } from './StudentBreaks'
import { StudentFormModal } from './StudentFormModal'
import { StudentLessonHistory } from './StudentLessonHistory'

export function StudentDetailPage() {
  const { studentId = '' } = useParams()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { notifySuccess, notifyError } = useToast()
  const canArchive = !isTeacher(user)

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isArchiveOpen, setIsArchiveOpen] = useState(false)
  const [noteContent, setNoteContent] = useState('')
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null)

  const studentQuery = useQuery({
    queryKey: ['students', studentId],
    queryFn: () => studentsApi.get(studentId),
    enabled: studentId !== '',
  })

  const notesQuery = useQuery({
    queryKey: ['students', studentId, 'notes'],
    queryFn: () => studentsApi.notes(studentId),
    enabled: studentId !== '',
  })

  const addNote = useMutation({
    mutationFn: () => studentsApi.addNote(studentId, noteContent.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['students', studentId, 'notes'] })
      setNoteContent('')
      notifySuccess('Notatka została dodana.')
    },
    onError: (caught: unknown) => {
      notifyError(caught instanceof ApiError ? caught.message : 'Nie udało się dodać notatki.')
    },
  })

  const deleteNote = useMutation({
    mutationFn: (noteId: string) => studentsApi.deleteNote(studentId, noteId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['students', studentId, 'notes'] })
      notifySuccess('Notatka została usunięta.')
      setNoteToDelete(null)
    },
    onError: (caught: unknown) => {
      notifyError(caught instanceof ApiError ? caught.message : 'Nie udało się usunąć notatki.')
      setNoteToDelete(null)
    },
  })

  const archive = useMutation({
    mutationFn: () => studentsApi.archive(studentId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['students'] })
      notifySuccess('Uczeń w archiwum. Jego przyszłe zajęcia zniknęły z grafiku.')
      setIsArchiveOpen(false)
    },
    onError: (caught: unknown) => {
      notifyError(caught instanceof ApiError ? caught.message : 'Nie udało się zarchiwizować.')
      setIsArchiveOpen(false)
    },
  })

  if (studentQuery.isPending) return <FullScreenLoader label="Wczytywanie karty ucznia…" />

  if (studentQuery.isError) {
    const isNotFound = studentQuery.error instanceof ApiError && studentQuery.error.isNotFound
    return (
      <Alert tone="error" title={isNotFound ? 'Nie znaleziono ucznia' : 'Błąd wczytywania'}>
        {isNotFound
          ? 'Uczeń nie istnieje albo nie masz do niego dostępu.'
          : 'Spróbuj odświeżyć stronę.'}{' '}
        <Link to="/uczniowie" className="font-bold text-primary underline">
          Wróć do listy
        </Link>
      </Alert>
    )
  }

  const student = studentQuery.data
  const age = ageFrom(student.birth_date)
  const notes = notesQuery.data ?? []

  return (
    <>
      <Link
        to="/uczniowie"
        className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-on-surface-variant transition-colors hover:text-primary"
      >
        <ArrowLeft size={16} aria-hidden="true" />
        Wróć do listy uczniów
      </Link>

      <PageHeader
        title={student.full_name}
        breadcrumb={['Szkoła', 'Uczniowie', student.full_name]}
        action={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              icon={<Pencil size={16} />}
              onClick={() => setIsFormOpen(true)}
            >
              Edytuj dane
            </Button>
            {canArchive && student.status !== 'ARCHIVED' && (
              <Button
                variant="ghost"
                icon={<Archive size={16} />}
                onClick={() => setIsArchiveOpen(true)}
              >
                Archiwizuj
              </Button>
            )}
          </div>
        }
      />

      {/* Patrz komentarz w TeacherDetailPage: bez `min-w-0` kolumna siatki
          rozpycha stronę na wąskim ekranie. */}
      <div className="grid gap-6 [&>*]:min-w-0 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader title="Dane ucznia" />
            <div className="space-y-6 p-6">
              <div className="flex flex-wrap items-center gap-4">
                <Avatar name={student.full_name} size="lg" />
                <div className="flex flex-wrap items-center gap-2">
                  <StudentStatusBadge status={student.status} />
                  <SkillLevelBadge level={student.level} />
                  <InstrumentBadges instruments={student.instruments} />
                  {student.is_traveling && (
                    <Badge tone="neutral">
                      <Car size={13} aria-hidden="true" />
                      Dojazd
                    </Badge>
                  )}
                </div>
              </div>

              <dl className="grid gap-4 sm:grid-cols-2">
                <Detail
                  icon={<Cake size={16} />}
                  label="Data urodzenia"
                  value={
                    student.birth_date
                      ? `${formatDate(student.birth_date)}${age !== null ? ` (${age} ${plural(age, 'rok', 'lata', 'lat')})` : ''}`
                      : '—'
                  }
                />
                <Detail
                  icon={<CalendarPlus size={16} />}
                  label="Data zapisu"
                  value={formatDate(student.enrolled_on)}
                />
                <Detail icon={<Phone size={16} />} label="Telefon" value={student.phone ?? '—'} />
                <Detail icon={<Mail size={16} />} label="E-mail" value={student.email ?? '—'} />
                {student.is_traveling && (
                  <Detail
                    icon={<Car size={16} />}
                    label="Adres dojazdu"
                    value={student.address ?? '—'}
                  />
                )}
              </dl>
            </div>
          </Card>

          <Card>
            <CardHeader title="Opiekun" />
            <div className="p-6">
              {student.guardian_name || student.guardian_phone || student.guardian_email ? (
                <dl className="grid gap-4 sm:grid-cols-3">
                  <Detail
                    icon={<UserIcon size={16} />}
                    label="Imię i nazwisko"
                    value={student.guardian_name ?? '—'}
                  />
                  <Detail
                    icon={<Phone size={16} />}
                    label="Telefon"
                    value={student.guardian_phone ?? '—'}
                  />
                  <Detail
                    icon={<Mail size={16} />}
                    label="E-mail"
                    value={student.guardian_email ?? '—'}
                  />
                </dl>
              ) : (
                <p className="text-sm text-on-surface-variant">Nie zapisano danych opiekuna.</p>
              )}

              {(student.guardian2_name || student.guardian2_phone || student.guardian2_email) && (
                <>
                  <p className="mb-3 mt-6 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
                    Drugi opiekun
                  </p>
                  <dl className="grid gap-4 sm:grid-cols-3">
                    <Detail
                      icon={<UserIcon size={16} />}
                      label="Imię i nazwisko"
                      value={student.guardian2_name ?? '—'}
                    />
                    <Detail
                      icon={<Phone size={16} />}
                      label="Telefon"
                      value={student.guardian2_phone ?? '—'}
                    />
                    <Detail
                      icon={<Mail size={16} />}
                      label="E-mail"
                      value={student.guardian2_email ?? '—'}
                    />
                  </dl>
                </>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Notatki nauczycieli" />
            <div className="p-6">
              <form
                onSubmit={(event: FormEvent) => {
                  event.preventDefault()
                  if (noteContent.trim()) addNote.mutate()
                }}
                className="mb-6 space-y-3"
              >
                <label htmlFor="nowa-notatka" className="block text-sm font-semibold">
                  Nowy wpis
                </label>
                <textarea
                  id="nowa-notatka"
                  value={noteContent}
                  onChange={(event) => setNoteContent(event.target.value)}
                  rows={3}
                  maxLength={4000}
                  placeholder="Postępy, materiał do przećwiczenia, uwagi z zajęć…"
                  className="w-full rounded-lg border-2 border-transparent bg-surface-container-low px-4 py-3 text-sm transition-colors placeholder:text-on-surface-variant/50 focus:border-primary focus:bg-surface focus:outline-none"
                />
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    size="sm"
                    isLoading={addNote.isPending}
                    disabled={noteContent.trim() === ''}
                  >
                    Dodaj notatkę
                  </Button>
                </div>
              </form>

              {notes.length === 0 ? (
                <p className="rounded-lg bg-surface-container-low p-4 text-sm text-on-surface-variant">
                  Nie ma jeszcze żadnych wpisów. Notatki są widoczne dla prowadzącego i
                  administracji — pomagają przy zastępstwie lub zmianie nauczyciela.
                </p>
              ) : (
                <ul className="space-y-4">
                  {notes.map((note) => (
                    <li key={note.id} className="rounded-lg border border-outline/10 p-4">
                      <div className="mb-2 flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-bold text-on-surface">{note.author_name}</p>
                          <p className="text-xs text-on-surface-variant">
                            {formatDateTime(note.created_at)}
                          </p>
                        </div>
                        {(note.author_id === user?.id || !isTeacher(user)) && (
                          <IconButton
                            label="Usuń notatkę"
                            icon={<Trash2 size={15} />}
                            onClick={() => setNoteToDelete(note.id)}
                            className="hover:bg-error-container hover:text-error"
                          />
                        )}
                      </div>
                      <p className="whitespace-pre-wrap text-sm leading-relaxed text-on-surface-variant">
                        {note.content}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>

          <StudentBreaks studentId={studentId} breaks={student.breaks} />
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Nauka" />
            <div className="space-y-4 p-6">
              <Detail label="Oddział" value={student.location.name} />
              <Detail label="Prowadzący" value={student.teacher?.full_name ?? 'Nie przypisano'} />
              <Detail
                label="Instrumenty"
                value={
                  student.instruments.length > 0
                    ? student.instruments.map((item) => item.name).join(', ')
                    : 'Nie wybrano'
                }
              />
            </div>
          </Card>

          {student.notes && (
            <Card>
              <CardHeader title="Uwagi administracyjne" />
              <p className="whitespace-pre-wrap p-6 text-sm leading-relaxed text-on-surface-variant">
                {student.notes}
              </p>
            </Card>
          )}

          <Card>
            <CardHeader title="Historia lekcji" />
            <StudentLessonHistory studentId={studentId} />
          </Card>
        </div>
      </div>

      <StudentFormModal
        open={isFormOpen}
        student={student}
        onClose={() => {
          setIsFormOpen(false)
          void queryClient.invalidateQueries({ queryKey: ['students', studentId] })
        }}
      />

      <ConfirmDialog
        open={isArchiveOpen}
        title="Przenieść ucznia do archiwum?"
        message={`${student.full_name} zniknie z bieżących list, a jego zaplanowane zajęcia zostaną usunięte z grafiku — zwolnią salę i termin u nauczyciela. Historia lekcji, obecności i płatności pozostanie nienaruszona. Ucznia można później przywrócić, zmieniając status, ale terminy trzeba będzie wpisać od nowa.`}
        confirmLabel="Archiwizuj"
        isLoading={archive.isPending}
        onConfirm={() => archive.mutate()}
        onClose={() => setIsArchiveOpen(false)}
      />

      <ConfirmDialog
        open={noteToDelete !== null}
        title="Usunąć notatkę?"
        message="Wpisu nie da się przywrócić."
        confirmLabel="Usuń"
        isLoading={deleteNote.isPending}
        onConfirm={() => noteToDelete && deleteNote.mutate(noteToDelete)}
        onClose={() => setNoteToDelete(null)}
      />
    </>
  )
}

function Detail({ icon, label, value }: { icon?: React.ReactNode; label: string; value: string }) {
  return (
    <div>
      <dt className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
        {icon && <span aria-hidden="true">{icon}</span>}
        {label}
      </dt>
      <dd className="mt-1.5 break-words text-sm font-semibold text-on-surface">{value}</dd>
    </div>
  )
}
